// IDBBlobCache - model bytes (tiles, sidecars, fragments, packs) kept in
// IndexedDB between visits, keyed by URL (which carries ?v=<export>).
//
// WHY THIS WAS REWRITTEN (2026-09-17). On a SpaceX cold load the viewer
// requested no geometry for ~55 s, and the reason was this cache, not the
// network:
//   - get() opened a READWRITE transaction (to bump lastAccess), and
//     IndexedDB runs readwrite transactions on a store one at a time - so
//     every lookup, of every fragment and pack, queued behind every write,
//     each with a durable commit. With 1,377 sidecars being written, no
//     pack could even be looked up until the last one had been committed
//     (probe: fetch queue empty, 0 fetches active, 1,015 packs "in flight"
//     waiting on IndexedDB).
//   - the first get/put walked the WHOLE store with a cursor that loads
//     every stored blob, just to add up sizes. With gigabytes cached - the
//     people who use the viewer most - the first request waited for that.
//
// Now:
//   - reads are READONLY transactions (they run in parallel) and give up
//     after `readTimeoutMs`, counting as a miss: the cache can speed a
//     load up, never hold one back;
//   - writes are queued and committed together every `flushMs` in one
//     transaction, with relaxed durability where supported (this is a
//     cache: losing the last second of it costs a re-download);
//   - last-access times and sizes live in a small `access` store (url ->
//     { lastAccess, sizeBytes }) and the running total in `meta`, so
//     neither LRU bumps nor accounting ever read or rewrite a blob;
//   - a new database name. The old one ('bidlight-streaming') is deleted
//     in the background: it holds combined fragment GLBs the viewer no
//     longer requests since packs, and migrating it would mean reading it.
//   - (round 11) a flush is written in CHUNKS of at most flushChunkBytes,
//     one transaction each, with a yield to the event loop between them.
//     Storing a blob copies its bytes on the main thread (structured clone,
//     inside the put), and one flush used to put everything queued - up to
//     100 MB measured in ONE task on a staging cold load (headless,
//     2026-10-17) - so the write completions (minified `e.onsuccess`) were
//     1.5-1.9 s of main-thread self time in Tom's slow r10 runs. Entries
//     being written stay readable (`_writing`) until their chunk commits.
//
// Public surface is unchanged: get, put, has, clearAll, stats, and the
// `_skipNextReads` flag ManifestLoader / cacheFlags set.
const DB_NAME = 'bidlight-streaming-2';
const LEGACY_DB_NAMES = ['bidlight-streaming'];
const DB_VERSION = 1;
const BLOBS = 'blobs';
const ACCESS = 'access';
const META = 'meta';
const DEFAULT_MAX_ENTRY_BYTES = 64 * 1024 * 1024;        // 64 MB per blob
const DEFAULT_MAX_TOTAL_BYTES = 4 * 1024 * 1024 * 1024;  // 4 GB total budget
const DEFAULT_EVICT_TARGET_PCT = 0.85;                   // after sweep, occupy 85% of cap

export default class IDBBlobCache {
  constructor({
    dbName = DB_NAME,
    maxEntryBytes = DEFAULT_MAX_ENTRY_BYTES,
    maxTotalBytes = DEFAULT_MAX_TOTAL_BYTES,
    evictTargetPct = DEFAULT_EVICT_TARGET_PCT,
    readTimeoutMs = 250,
    flushMs = 1000,
    flushBytes = 32 * 1024 * 1024,
    // At most this many bytes (and flushChunkEntries entries) are stored per
    // transaction; one entry larger than that goes alone.
    flushChunkBytes = 4 * 1024 * 1024,
    flushChunkEntries = 64,
    deleteLegacy = true,
    idb = (typeof indexedDB !== 'undefined' ? indexedDB : null),
  } = {}) {
    this.dbName = dbName;
    this.maxEntryBytes = maxEntryBytes;
    this.maxTotalBytes = maxTotalBytes;
    this.evictTargetBytes = Math.floor(maxTotalBytes * evictTargetPct);
    this.readTimeoutMs = readTimeoutMs;
    this.flushMs = flushMs;
    this.flushBytes = flushBytes;
    this.flushChunkBytes = flushChunkBytes;
    this.flushChunkEntries = flushChunkEntries;
    this._idb = idb;

    this._db = null;
    this._available = !!idb;
    this._totalBytes = 0;
    this._pending = new Map();   // url -> entry waiting to be written
    this._writing = new Map();   // url -> entry taken by a flush, not committed yet
    this._gen = 0;               // bumped by clearAll: a flush in progress stops writing
    this._pendingBytes = 0;
    this._touched = new Map();   // url -> lastAccess waiting to be written
    this._flushTimer = null;
    this._flushing = null;
    this._stats = { hits: 0, misses: 0, readTimeouts: 0, flushes: 0, written: 0, chunks: 0, maxChunkBytes: 0 };

    if (this._available) {
      this._ready = this._open().catch((err) => {
        console.warn('[IDBBlobCache] IndexedDB open failed, disabling cache:', err);
        this._available = false;
        this._db = null;
      });
      if (deleteLegacy) this._deleteLegacy();
    } else {
      this._ready = Promise.resolve();
    }
  }

  _open() {
    return new Promise((resolve, reject) => {
      const req = this._idb.open(this.dbName, DB_VERSION);
      req.onupgradeneeded = (ev) => {
        const db = ev.target.result;
        if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS, { keyPath: 'url' });
        if (!db.objectStoreNames.contains(ACCESS)) {
          const access = db.createObjectStore(ACCESS, { keyPath: 'url' });
          access.createIndex('lastAccess', 'lastAccess', { unique: false });
        }
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'key' });
      };
      req.onsuccess = () => {
        this._db = req.result;
        // Another tab upgrading or deleting: let go rather than block it.
        this._db.onversionchange = () => { try { this._db.close(); } catch (e) { /* closed */ } this._db = null; this._available = false; };
        // The running total: one small record.
        try {
          const tx = this._db.transaction(META, 'readonly');
          const r = tx.objectStore(META).get('totalBytes');
          r.onsuccess = () => { this._totalBytes = (r.result && r.result.value) || 0; resolve(); };
          r.onerror = () => resolve();
        } catch (e) { resolve(); }
      };
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
  }

  _deleteLegacy() {
    const del = () => {
      for (const name of LEGACY_DB_NAMES) {
        try { this._idb.deleteDatabase(name); } catch (e) { /* not there, or not allowed */ }
      }
    };
    // Out of the way of the first load.
    if (typeof setTimeout === 'function') setTimeout(del, 30000); else del();
  }

  _tx(stores, mode) {
    try {
      return mode === 'readwrite'
        ? this._db.transaction(stores, mode, { durability: 'relaxed' })
        : this._db.transaction(stores, mode);
    } catch (e) {
      // Browsers without the options argument.
      return this._db.transaction(stores, mode);
    }
  }

  // Returns ArrayBuffer | null. A read that takes longer than
  // readTimeoutMs is a miss (the caller goes to the network).
  // `opts.timeoutMs` overrides the wait for one read: GLBFetcher passes a
  // longer one for a URL it stored earlier in the session, where a hit is
  // almost certain and a timeout costs a download.
  async get(url, opts = undefined) {
    const waitMs = opts && opts.timeoutMs > 0 ? opts.timeoutMs : this.readTimeoutMs;
    const queued = this._pending.get(url) || this._writing.get(url);
    if (queued) { this._stats.hits++; return queued.bytes; }
    await this._ready;
    if (!this._available || !this._db) return null;
    const read = new Promise((resolve) => {
      let tx;
      try { tx = this._tx(BLOBS, 'readonly'); } catch (e) { resolve(null); return; }
      const req = tx.objectStore(BLOBS).get(url);
      req.onsuccess = () => resolve(req.result ? req.result.bytes : null);
      req.onerror = () => resolve(null);
    });
    let timer = null;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => { this._stats.readTimeouts++; resolve(null); }, waitMs);
    });
    const bytes = await Promise.race([read, timeout]);
    clearTimeout(timer);
    if (bytes) {
      this._stats.hits++;
      this._touched.set(url, Date.now());   // LRU bump, written with the next flush
      this._scheduleFlush();
    } else {
      this._stats.misses++;
    }
    return bytes;
  }

  // Bytes: ArrayBuffer. Queued; written with the next flush.
  async put(url, bytes, meta = {}) {
    if (!this._available) return;
    if (!bytes || !(bytes instanceof ArrayBuffer)) return;
    if (bytes.byteLength > this.maxEntryBytes) return;
    const prev = this._pending.get(url);
    if (prev) this._pendingBytes -= prev.sizeBytes;
    this._pending.set(url, { url, bytes, sizeBytes: bytes.byteLength, contentEncoding: meta.contentEncoding || null });
    this._pendingBytes += bytes.byteLength;
    this._scheduleFlush(this._pendingBytes >= this.flushBytes);
  }

  _scheduleFlush(now = false) {
    if (now) {
      if (this._flushTimer) { clearTimeout(this._flushTimer); this._flushTimer = null; }
      this.flush();
      return;
    }
    if (this._flushTimer) return;
    this._flushTimer = setTimeout(() => { this._flushTimer = null; this.flush(); }, this.flushMs);
  }

  // Write everything queued, a chunk per transaction, yielding between
  // chunks (see the header). Safe to call any time; a call while a flush
  // runs joins it, and what was queued meanwhile is flushed after it.
  async flush() {
    if (this._flushing) return this._flushing;
    if (!this._pending.size && !this._touched.size) return undefined;
    this._flushing = (async () => {
      await this._ready;
      if (!this._available || !this._db) { this._pending.clear(); this._pendingBytes = 0; this._touched.clear(); return; }
      const gen = this._gen;
      const entries = [...this._pending.values()];
      for (const e of entries) this._writing.set(e.url, e);
      let touched = [...this._touched.entries()].filter(([u]) => !this._pending.has(u));
      this._pending.clear();
      this._pendingBytes = 0;
      this._touched.clear();
      let i = 0;
      let first = true;
      while (i < entries.length || (first && touched.length)) {
        if (gen !== this._gen) break;                       // cleared meanwhile
        if (!first) await new Promise((r) => setTimeout(r, 0));
        if (gen !== this._gen || !this._db) break;
        const chunk = [];
        let bytes = 0;
        while (i < entries.length && chunk.length < this.flushChunkEntries
          && (chunk.length === 0 || bytes + entries[i].sizeBytes <= this.flushChunkBytes)) {
          bytes += entries[i].sizeBytes;
          chunk.push(entries[i++]);
        }
        await this._writeChunk(chunk, touched);
        for (const e of chunk) if (this._writing.get(e.url) === e) this._writing.delete(e.url);
        if (bytes > this._stats.maxChunkBytes) this._stats.maxChunkBytes = bytes;
        this._stats.chunks++;
        touched = [];
        first = false;
      }
      for (const e of entries) if (this._writing.get(e.url) === e) this._writing.delete(e.url);
      this._stats.flushes++;
      if (this._totalBytes > this.maxTotalBytes) this._evictLRU().catch(() => {});
    })();
    try { return await this._flushing; } finally {
      this._flushing = null;
      if (this._pending.size || this._touched.size) this._scheduleFlush();
    }
  }

  // One transaction: these entries (and LRU bumps), then the running total.
  _writeChunk(entries, touched) {
    return new Promise((resolve) => {
      let tx;
      try { tx = this._tx([BLOBS, ACCESS, META], 'readwrite'); } catch (e) { resolve(); return; }
      const blobs = tx.objectStore(BLOBS);
      const access = tx.objectStore(ACCESS);
      const now = Date.now();
      let delta = 0;
      for (const e of entries) {
        // The old size comes from the small access row, never the blob.
        const r = access.get(e.url);
        r.onsuccess = () => {
          delta += e.sizeBytes - ((r.result && r.result.sizeBytes) || 0);
          blobs.put(e);
          access.put({ url: e.url, lastAccess: now, sizeBytes: e.sizeBytes });
        };
      }
      for (const [url, at] of touched) {
        const r = access.get(url);
        r.onsuccess = () => { if (r.result) access.put({ ...r.result, lastAccess: at }); };
      }
      tx.oncomplete = () => {
        this._totalBytes = Math.max(0, this._totalBytes + delta);
        this._stats.written += entries.length;
        // The total, for the next visit (its own tiny transaction).
        try { this._tx(META, 'readwrite').objectStore(META).put({ key: 'totalBytes', value: this._totalBytes }); } catch (e) { /* best effort */ }
        resolve();
      };
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  }

  async has(url) {
    if (this._pending.has(url) || this._writing.has(url)) return true;
    await this._ready;
    if (!this._available || !this._db) return false;
    return new Promise((resolve) => {
      let tx;
      try { tx = this._tx(ACCESS, 'readonly'); } catch (e) { resolve(false); return; }
      const req = tx.objectStore(ACCESS).count(url);
      req.onsuccess = () => resolve(req.result > 0);
      req.onerror = () => resolve(false);
    });
  }

  // Least recently used first, from the access rows only, until the total
  // is back under the eviction target.
  // One sweep at a time: a second one started before the first commits
  // would read the stale total and evict again.
  _evictLRU() {
    if (!this._evicting) {
      this._evicting = this._evictOnce().finally(() => { this._evicting = null; });
    }
    return this._evicting;
  }

  async _evictOnce() {
    if (!this._available || !this._db) return undefined;
    return new Promise((resolve) => {
      let tx;
      try { tx = this._tx([BLOBS, ACCESS, META], 'readwrite'); } catch (e) { resolve(); return; }
      const blobs = tx.objectStore(BLOBS);
      const access = tx.objectStore(ACCESS);
      let target = this._totalBytes - this.evictTargetBytes;
      if (target <= 0) { resolve(); return; }
      let freed = 0;
      const cursorReq = access.index('lastAccess').openCursor();
      cursorReq.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (!cursor || target <= 0) return;
        const size = cursor.value.sizeBytes || 0;
        blobs.delete(cursor.value.url);
        cursor.delete();
        freed += size;
        target -= size;
        cursor.continue();
      };
      tx.oncomplete = () => {
        this._totalBytes = Math.max(0, this._totalBytes - freed);
        try { this._tx(META, 'readwrite').objectStore(META).put({ key: 'totalBytes', value: this._totalBytes }); } catch (e) { /* best effort */ }
        resolve();
      };
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  }

  stats() {
    return {
      available: this._available,
      totalBytes: this._totalBytes,
      maxTotalBytes: this.maxTotalBytes,
      maxEntryBytes: this.maxEntryBytes,
      pending: this._pending.size,
      writing: this._writing.size,
      ...this._stats,
    };
  }

  // Drop every entry. Used when ManifestLoader detects a new export
  // version, and by ?cache=clear. Returns the number of deleted entries.
  async clearAll() {
    this._gen++;
    this._pending.clear();
    this._writing.clear();
    this._pendingBytes = 0;
    this._touched.clear();
    await this._ready;
    if (!this._available || !this._db) return 0;
    return new Promise((resolve) => {
      let tx;
      try { tx = this._tx([BLOBS, ACCESS, META], 'readwrite'); } catch (e) { resolve(0); return; }
      let before = 0;
      const countReq = tx.objectStore(ACCESS).count();
      countReq.onsuccess = () => { before = countReq.result; };
      tx.objectStore(BLOBS).clear();
      tx.objectStore(ACCESS).clear();
      tx.objectStore(META).put({ key: 'totalBytes', value: 0 });
      tx.oncomplete = () => { this._totalBytes = 0; resolve(before); };
      tx.onerror = () => resolve(0);
      tx.onabort = () => resolve(0);
    });
  }
}
